import React, { useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { cover } from 'polished';
import { clearInterval, setInterval } from 'safe-timers';
import styled from 'styled-components';

import { users } from 'api';
import { secondsToHms } from 'helpers/dates';
import { getSessionTimeout } from 'helpers/sessionTimeout';
import { flexCenter } from 'helpers/styles/mixins';
import { useGetLastRequestTimestamp } from 'store/sessionExpiration/selectors';
import { useGetAdviceRoomConnectionState } from 'store/videoChat/selectors';
import GenericLoader from 'components/GenericLoader/GenericLoader';
import { BasicTooltip, TooltipPlacement } from 'components/GenericTooltip';
import { RoomConnectionState } from 'components/VideoChat/RoomConnectionState';

const Wrapper = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.dbSmallMd};
`;

const LoaderWrapper = styled.div`
  ${cover()};
  ${flexCenter()};
`;

const Button = styled.button`
  text-align: right;
  color: ${({ theme }) => theme.colors.main};

  &:disabled {
    cursor: default;
  }
`;

export function SessionTimer() {
  const [secondsLeft, setSecondsLeft] = useState(getSessionTimeout() * 60);
  const [isExtending, setIsExtending] = useState(false);
  const lastRequestTimestamp = useGetLastRequestTimestamp();
  const connectionState = useGetAdviceRoomConnectionState();
  const isConnected = connectionState === RoomConnectionState.Connected;

  useEffect(() => {
    if (isConnected) {
      return;
    }

    const calculateSecondsLeft = () => {
      const result = getSessionTimeout() * 60 - (Date.now() - lastRequestTimestamp) / 1000;
      return result > 0 ? result : 0;
    };

    setSecondsLeft(calculateSecondsLeft());

    const sessionIntervalTimer = setInterval(() => {
      setSecondsLeft(calculateSecondsLeft());
    }, 1000);

    return () => {
      clearInterval(sessionIntervalTimer);
    };
  }, [lastRequestTimestamp, isConnected]);

  const extendSession = useCallback(() => {
    setIsExtending(true);
    users()
      .me()
      .GET()
      .finally(() => {
        setIsExtending(false);
      });
  }, []);

  return (
    <BasicTooltip
      disabled={isConnected}
      tooltipMessage={<FormattedMessage id="shared.sessionExpiresInTooltip" />}
      position={TooltipPlacement.Bottom}
    >
      <Wrapper>
        <Button
          type="button"
          disabled={isExtending || isConnected}
          onClick={extendSession}
          data-test="session-timer"
        >
          {isConnected ? (
            <FormattedMessage
              id="shared.sessionWillNotExpire"
              values={{
                br: <br />,
              }}
            />
          ) : (
            <FormattedMessage
              id="shared.sessionExpiresIn"
              values={{ br: <br />, value: secondsToHms(secondsLeft) }}
            />
          )}
        </Button>

        {isExtending && (
          <LoaderWrapper>
            <GenericLoader size={16} isVisible />
          </LoaderWrapper>
        )}
      </Wrapper>
    </BasicTooltip>
  );
}
